import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { t } from 'ttag'
import styled from 'styled-components/macro'
import 'react-dropdown/style.css'
import NutsModal from 'components/modals/NutsModal'
import { getRegionsAsList, updateSelectedFilters } from 'store/actions/filters'
import PropTypes from 'prop-types'

const macroRegionLabels = {
  eusair: t`Adriatic-Ionian Region (EUSAIR)`,
  eusalp: t`Alpine Region (EUSALP)`,
  eusdr: t`Danube Region (EUSDR)`,
  eusbsr: t`Baltic Sea Region (EUSBSR)`
}

const StyledCustomDropdown = styled.div`
  font-family: 'Open Sans';
  font-size: 12px;
  margin-top: 8px;
  position: relative;

  .Dropdown-control {
    background-color: #4a4a4a;
    color: white;
    padding: 12px;
    border-radius: 4px;
  }
  .Dropdown-menu {
    border-radius: 0 0 4px 4px;
  }
  .Dropdown-arrow {
    border: none;
    &::after {
      font-family: 'Font Awesome 6 Pro' !important;
      content: '\f078' !important;
      font-size: 22px;
      position: absolute;
      right: 2px;
      top: -7px;
    }
  }
  .Dropdown-option {
    width: 40%;
    text-align: right;
  }
  .Dropdown-option-button {
    display: inline-flex;
    float: right;
    width: 85%;
    font-family: Roboto;
    transition: box-shadow 280ms cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow:
      0px 3px 1px -2px rgba(0, 0, 0, 0.2),
      0px 2px 2px 0px rgba(0, 0, 0, 0.14),
      0px 1px 5px 0px rgba(0, 0, 0, 0.12);
    padding: 5px;
    outline: none;
    border-radius: 3px;
    border: 0;
    color: #f7f7f9;
    background-color: #003399;
    font-size: 13px;
    line-height: 20px;
    margin: 0;
    justify-content: center;
    &:hover {
      background: rgb(1, 58, 175);
      box-shadow:
        0px 2px 4px -1px rgba(0, 0, 0, 0.2),
        0px 4px 5px 0px rgba(0, 0, 0, 0.14),
        0px 1px 10px 0px rgba(0, 0, 0, 0.12);
      cursor: pointer;
    }
    &:focus {
      background: rgb(76, 113, 184);
      box-shadow:
        0px 2px 4px -1px rgba(0, 0, 0, 0.2),
        0px 4px 5px 0px rgba(0, 0, 0, 0.14),
        0px 1px 10px 0px rgba(0, 0, 0, 0.12);
    }
    &:active {
      background: rgb(75, 112, 183);
      box-shadow:
        0px 5px 5px -3px rgba(0, 0, 0, 0.2),
        0px 8px 10px 1px rgba(0, 0, 0, 0.14),
        0px 3px 14px 2px rgba(0, 0, 0, 0.12);
    }
  }
  .Dropdown-option-wrapper {
    padding: 8px 10px;
    display: flex;
    align-items: center;

    &:hover {
      background-color: #f2f9fc;
      color: #333;
      cursor: pointer;
    }
  }
`

const customDropdownListClosed = {
  display: 'none'
}
const customDropdownListOpened = {
  display: 'block'
}
function NutsDropdown({ type, values }) {
  const [isListOpened, setIsListOpened] = useState(false)
  const [isMapModalOpened, setIsMapModalOpened] = useState(false)
  const [selectedRegions, setSelectedRegions] = useState([])
  const filters = useSelector(state => state.filters)
  const { currentSearchable, selectedFilters } = filters
  const regionsByStrategy = filters.availableFilters.projects.regions_by_strategy
  const filterObject = selectedFilters[currentSearchable][type]
  const selectedSearchType = filterObject[values[2]]
  const dispatch = useDispatch()

  function getUpdatedNuts(fetchedNuts) {
    const updatedNuts = fetchedNuts.map(nut => ({
      id: nut.nuts_id,
      label: nut.name,
      paths: nut.paths,
      type: nut.type
    }))

    return updatedNuts
  }

  function getNutsByArea(area) {
    let filteredNuts = []

    regionsByStrategy[area].forEach(nut => {
      filteredNuts.push({
        id: nut.nuts_id,
        label: nut.english_alias
          ? `[${nut.nuts_id}] ${nut.english_alias} / ${nut.name}`
          : `[${nut.nuts_id}] ${nut.name}`,
        type: nut.type
      })
    })

    return filteredNuts
  }

  function selectAreas(newArea) {
    // Currently selected areas
    const [leadValue, partnerValue] = values

    if (selectedSearchType === 'both') {
      const filterPartnerValue = [...filterObject[partnerValue]]
      const filterLeadValue = [...filterObject[leadValue]]
      const partnerNewAreas = filterObject.selectedAreas[partnerValue]
        ? [...filterObject.selectedAreas[partnerValue]]
        : []
      const leadNewAreas = filterObject.selectedAreas[leadValue]
        ? [...filterObject.selectedAreas[leadValue]]
        : []

      if (!partnerNewAreas.includes(newArea)) {
        partnerNewAreas.push(newArea)
      }

      partnerNewAreas.forEach(area => {
        regionsByStrategy[area].forEach(nut => {
          if (!filterPartnerValue.some(x => x.id === nut.nuts_id)) {
            filterPartnerValue.push({
              id: nut.nuts_id,
              label: nut.english_alias
                ? `[${nut.nuts_id}] ${nut.english_alias} / ${nut.name}`
                : `[${nut.nuts_id}] ${nut.name}`,
              type: nut.type
            })
          }
        })
      })

      const partnerData = {
        filterType: type,
        filterId: partnerValue,
        filterValue: filterPartnerValue
      }

      if (!leadNewAreas.includes(newArea)) {
        leadNewAreas.push(newArea)
      }

      leadNewAreas.forEach(area => {
        regionsByStrategy[area].forEach(nut => {
          if (!filterLeadValue.some(x => x.id === nut.nuts_id)) {
            filterLeadValue.push({
              id: nut.nuts_id,
              label: nut.english_alias
                ? `[${nut.nuts_id}] ${nut.english_alias} / ${nut.name}`
                : `[${nut.nuts_id}] ${nut.name}`,
              type: nut.type
            })
          }
        })
      })

      const leadData = {
        filterType: type,
        filterId: leadValue,
        filterValue: filterLeadValue
      }

      dispatch(updateSelectedFilters(partnerData))
      dispatch(updateSelectedFilters(leadData))

      const areaData = {
        filterType: type,
        filterId: 'selectedAreas',
        filterValue: {
          ...filterObject.selectedAreas,
          [leadValue]: leadNewAreas,
          [partnerValue]: partnerNewAreas
        }
      }

      dispatch(updateSelectedFilters(areaData))

      return
    }

    const filterId = selectedSearchType === 'projects' ? partnerValue : leadValue
    const filterValue = [...filterObject[filterId]]

    const newAreas = filterObject.selectedAreas[filterId]
      ? [...filterObject.selectedAreas[filterId]]
      : []

    if (!newAreas.includes(newArea)) {
      newAreas.push(newArea)
    }

    newAreas.forEach(area => {
      regionsByStrategy[area].forEach(nut => {
        if (!filterValue.some(x => x.id === nut.nuts_id)) {
          filterValue.push({
            id: nut.nuts_id,
            label: nut.english_alias
              ? `[${nut.nuts_id}] ${nut.english_alias} / ${nut.name}`
              : `[${nut.nuts_id}] ${nut.name}`,
            type: nut.type
          })
        }
      })
    })

    const data = {
      filterType: type,
      filterId,
      filterValue
    }

    dispatch(updateSelectedFilters(data))

    const areaData = {
      filterType: type,
      filterId: 'selectedAreas',
      filterValue: {
        ...filterObject.selectedAreas,
        [filterId]: newAreas
      }
    }

    dispatch(updateSelectedFilters(areaData))
  }

  function onItemClick(area) {
    if (area) {
      selectAreas(area)
      setIsListOpened(false)
    }
  }

  function onAreaSelect(area) {
    const filteredNuts = getNutsByArea(area)

    const noPaths = filteredNuts.filter(value => !value.paths)

    if (noPaths.length > 0) {
      const nutsIds = {}
      nutsIds.nuts_ids = []
      filteredNuts.forEach(nut => {
        nutsIds.nuts_ids.push(nut.id)
      })
      dispatch(getRegionsAsList(nutsIds)).then(response => {
        const updatedNuts = getUpdatedNuts(response.value)
        setSelectedRegions(updatedNuts)
        setIsMapModalOpened(true)
        setIsListOpened(false)
      })
    }
  }

  return (
    <>
      <StyledCustomDropdown className="custom-dropdown-wrapper">
        <div
          className="Dropdown-control"
          onBlur={() => setIsListOpened(false)}
          onClick={() => setIsListOpened(!isListOpened)}
        >
          <div className="Dropdown-placeholder">{t`Or choose full macro regions' areas`}</div>
          <div className="Dropdown-arrow-wrapper">
            <span className="Dropdown-arrow" />
          </div>
        </div>
        <div
          className="Dropdown-menu"
          style={isListOpened ? customDropdownListOpened : customDropdownListClosed}
        >
          {Object.keys(regionsByStrategy).map(key => (
            <div key={key} className="Dropdown-option-wrapper">
              <button
                className="Dropdown-option-button iEoOGk"
                value={key}
                onClick={() => onItemClick(key)}
              >
                {macroRegionLabels[key]}
              </button>
              <div className="Dropdown-option" id={key} onClick={() => onAreaSelect(key)}>
                Show on map
              </div>
            </div>
          ))}
        </div>
      </StyledCustomDropdown>
      <NutsModal
        modalOpen={isMapModalOpened}
        toggleModal={() => setIsMapModalOpened(prevState => !prevState)}
        selectedValues={selectedRegions}
        disableNutsSelect={true}
      />
    </>
  )
}

NutsDropdown.propTypes = {
  type: PropTypes.string,
  values: PropTypes.array
}

export default NutsDropdown
